import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChoiceChips } from "@/components/ui/choice-chips";
import { OptionSelect } from "@/components/ui/option-select";
import { CategoryIcon } from "@/lib/categoryIcons";
import { CaretDown, FunnelSimple, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Search plus four refinements.
 *
 * All five stacked full-width is a screen and a half of controls before the
 * first transaction on a phone, so only search stays out; the rest collapse
 * behind a toggle that carries a count, because a filter you cannot see is a
 * filter you forget you set. From `md` the row has the width to show
 * everything at once and the toggle disappears.
 */
const TYPE_OPTIONS = [
  { value: "all", label: "All", testid: "filter-type-all" },
  { value: "income", label: "Income", testid: "filter-type-income" },
  { value: "expense", label: "Expense", testid: "filter-type-expense" },
];

const ALL_CATEGORIES_OPTION = { value: "all", label: "All categories" };

function categoryOption(category) {
  return {
    value: category.name,
    label: category.name,
    icon: <CategoryIcon category={category} size={15} />,
  };
}

export default function TransactionFilters({ filters, categories, onChange }) {
  const [open, setOpen] = useState(false);
  const { search, type, category, startDate, endDate } = filters;

  const active = [
    type !== "all",
    category !== "all",
    Boolean(startDate),
    Boolean(endDate),
  ].filter(Boolean).length;

  const clearAll = () => {
    onChange("type", "all");
    onChange("category", "all");
    onChange("startDate", "");
    onChange("endDate", "");
  };

  return (
    <Card className="bg-card border-border p-4 rounded-lg">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-input"
            type="search"
            enterKeyHint="search"
            placeholder="Search…"
            value={search}
            onChange={(e) => onChange("search", e.target.value)}
            className="pl-9 bg-background"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={active ? `Filters, ${active} active` : "Filters"}
          data-testid="toggle-filters"
          className="relative h-9 w-9 shrink-0 border-border md:hidden"
        >
          <FunnelSimple size={16} />
          {active > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-income px-1 text-[10px] font-bold tabular-nums text-black">
              {active}
            </span>
          )}
          <CaretDown size={10} className={`absolute bottom-0.5 right-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </div>

      <div
        className={`${open ? "grid" : "hidden"} mt-3 grid-cols-1 gap-3 sm:grid-cols-2 md:mt-3 md:grid md:grid-cols-4`}
        data-testid="filter-fields"
      >
        <ChoiceChips
          label="Type"
          variant="segmented"
          options={TYPE_OPTIONS}
          value={type}
          onValueChange={(value) => onChange("type", value)}
        />

        <OptionSelect
          title="Category"
          options={[ALL_CATEGORIES_OPTION, ...categories.map(categoryOption)]}
          value={category}
          onValueChange={(value) => onChange("category", value)}
          data-testid="filter-category"
          className="bg-background"
        />

        {/* An empty date field shows no hint of which end of the range it is. */}
        <DateFilter label="From" value={startDate} onChange={(value) => onChange("startDate", value)} testid="filter-start-date" />
        <DateFilter label="To" value={endDate} onChange={(value) => onChange("endDate", value)} testid="filter-end-date" />

        {active > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="justify-start gap-1.5 text-muted-foreground md:hidden" data-testid="clear-filters">
            <X size={14} /> Clear filters
          </Button>
        )}
      </div>
    </Card>
  );
}

function DateFilter({ label, value, onChange, testid }) {
  return (
    <Label className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="w-9 shrink-0">{label}</span>
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-background text-foreground touch:h-11"
        data-testid={testid}
      />
    </Label>
  );
}
